// Split out of src/components/SettingsTab.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// SettingsTab.jsx re-exports everything it exported before, so existing imports keep working.

import { t as ct } from "../../lib/content.js";

// LB-265 (Alan, 12 Sep): "Consolidate Daily task reminder and morning task reminder into a
// single choice. Arguably it's overkill to have both settings and the risk is that the app
// becomes chatty." It was exactly that — the 7am heads-up and the evening streak nudge were
// independent, both defaulted on, and a child with unfinished tasks got buzzed twice a day
// about the same list. One setting now: pick the time of day, or off. One nudge, never two.
//
// The hour IS the choice, rather than a separate kind-of-reminder question, because the two
// differ only by when they land and what that makes sensible: at 7am nobody has had a chance
// to do anything yet, so it is a silent heads-up sent to every child with tasks; later in the
// day it is a nudge to whoever still has tasks left. The line under the chips says which one
// the parent has just chosen, so the difference is stated rather than discovered.
//
// Migration (see migrateState): the evening hour wins for any household that had both, since
// it is the one a parent actually picked from a list. Morning only survives where the evening
// reminder had been explicitly switched off.
const MORNING_REMINDER_HOUR = 7;
const EVENING_REMINDER_HOURS = [16, 17, 18, 19, 20];
const REMINDER_HOURS = [MORNING_REMINDER_HOUR, ...EVENING_REMINDER_HOURS];

export function taskReminderHourOf(settings) {
  const configured = settings?.taskReminderHour;
  if (configured === null) return null;
  return Number.isInteger(configured) ? configured : 18;
}

// Native Run C, Part 2 (decision 4, signed off from drawings): the Notifications page and the
// last setup step are GROUPED ROWS — a small uppercase grey label, one white card whose rows
// are divided by hairlines, an optional grey caption under it. The group owns the card; the
// children are rows. Settings' other categories keep their cards.
export function NotificationGroup({ label, caption, children, testId }) {
  return (
    <div data-testid={testId}>
      {label && <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 px-1 mb-1.5">{label}</div>}
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">{children}</div>
      {caption && <p className="text-xs text-slate-400 mt-1.5 px-1">{caption}</p>}
    </div>
  );
}

// The time chips, as the one row of the "Kids' task reminder" group. Native Run C removed the
// card's intro sentence and the per-time explainer under the chips (decision 4): what the
// choice means is now the group's one caption, and the chip that is pressed is the answer.
function TaskReminderCard({ state, update, showToast }) {
  const current = taskReminderHourOf(state.settings);

  function setHour(hour) {
    update((s) => { s.settings.taskReminderHour = hour; return s; });
    showToast(hour === null ? ct("toast.reminder_off", "Task reminder turned off") : ct("toast.reminder_set", "Task reminder set for {hour}:00", { hour }), "good");
  }

  return (
    <div className="px-4 py-3" data-testid="task-reminder-card">
      <div className="flex gap-1.5 flex-wrap">
        {REMINDER_HOURS.map((h) => (
          <button key={h} type="button" onClick={() => setHour(h)} aria-pressed={current === h}
            className={`text-xs font-bold rounded-lg px-3 py-1.5 ${current === h ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}>
            {h}:00
          </button>
        ))}
        <button type="button" onClick={() => setHour(null)} aria-pressed={current === null}
          className={`text-xs font-bold rounded-lg px-3 py-1.5 ${current === null ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}>
          Off
        </button>
      </div>
    </div>
  );
}

export { TaskReminderCard };
